import { forwardRef, useEffect, useState } from 'react';
import Select from 'react-select/dist/declarations/src/Select';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import { Flex, Text } from '@/app/ui/atoms';
import { Icon } from '@/app/ui/molecules';
import Colors from '@/app/styles/Colors';

export type Item = {
  value: string;
  label: string;
};

export type ItemGroup = {
  items: Item[];
};

interface InputTextAutocompleteProps {
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  tooltip?: boolean;
  items: Item[];
  defaultOption?: Item | Item[];
  mandatoryError?: boolean;
  labelColor?: string;
  onChange?: (value: string) => void;
  onChangeMulti?: (value: string[]) => void;
  multiple?: boolean;
}

const InputTextAutocomplete = forwardRef<Select<Item>, InputTextAutocompleteProps>(
  (props: InputTextAutocompleteProps, ref) => {
    const {
      disabled,
      label,
      placeholder,
      tooltip,
      items,
      defaultOption,
      mandatoryError,
      labelColor,
      onChange,
      onChangeMulti,
      multiple,
    } = props;

    const [currentValue, setCurrentValue] = useState<Item | Item[] | undefined>(defaultOption);

    const handleOnChange = (option: SingleValue<Item> | MultiValue<Item>) => {
      if (multiple) {
        if (!onChangeMulti || !(onChangeMulti instanceof Function)) return;
        const values = (option as MultiValue<Item>).map((item) => item.value);
        onChangeMulti(values);
        setCurrentValue(option as Item[]);
      } else {
        if (!onChange || !(onChange instanceof Function)) return;
        const { value } = option as Item;
        onChange(value);
        setCurrentValue(option as Item);
      }
    };

    useEffect(() => {
      setCurrentValue(defaultOption);
    }, [defaultOption]);

    return (
      <Flex.Container flexDirection="column" style={{ width: '100%', position: 'relative' }}>
        <Flex.Container gap="0.5rem" alignItems="center">
          <Text.LabelSmall
            htmlFor="title_input"
            style={{ color: mandatoryError ? Colors.red : labelColor, marginBottom: '2px' }}
          >
            {label}
          </Text.LabelSmall>
          {tooltip && <Icon.InfoIcon />}
        </Flex.Container>
        <ReactSelect
          isDisabled={disabled}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              borderRadius: 0,
              border: mandatoryError ? `1px solid ${Colors.red}` : '',
            }),
            option: (baseStyles) => ({
              ...baseStyles,
              color: Colors.black,
            }),
          }}
          isMulti={multiple}
          onChange={handleOnChange}
          ref={ref}
          value={currentValue || null}
          options={items}
          placeholder={placeholder}
          maxMenuHeight={160}
        />
      </Flex.Container>
    );
  },
);

export default InputTextAutocomplete;
